import React, { FC, useMemo } from 'react';
import { convertPieChartToECharts } from '../adapters/echartsAdapter';
import { EChartsContainer } from '../shared';
import { ChartSeries, PieChartProps } from '../types';

/**
 * ScalarPieChart
 *
 * A generic pie chart component with a library-agnostic API.
 * Internally uses ECharts, but the API is designed to be easily swappable.
 *
 * @example
 * ```tsx
 * <ScalarPieChart
 *   data={[
 *     { x: 'Category A', y: 30 },
 *     { x: 'Category B', y: 50 },
 *     { x: 'Category C', y: 20 },
 *   ]}
 *   config={{
 *     title: 'Distribution',
 *   }}
 *   showPercentages={true}
 * />
 * ```
 */
const ScalarPieChart: FC<PieChartProps> = ({
  data,
  config,
  label,
  showLabels = true,
  showPercentages = true,
  innerRadius = 0,
  className,
  style,
  loading = false,
  error = null,
  notMerge = false,
  onDataPointClick,
  onChartReady,
}) => {
  const echartsOption = useMemo(
    () =>
      convertPieChartToECharts({
        data,
        config,
        label,
        showLabels,
        showPercentages,
        innerRadius,
      }),
    [data, config, label, showLabels, showPercentages, innerRadius]
  );

  const onEvents = useMemo((): Record<string, (params: unknown) => void> | undefined => {
    if (!onDataPointClick) return undefined;

    return {
      click: (params: unknown) => {
        const p = params as { name?: string };
        const dataPoint = data.find(point => String(point.x) === p.name);
        if (dataPoint) {
          const seriesItem: ChartSeries = {
            id: 'pie',
            label: p.name ?? '',
            data: [dataPoint],
          };
          onDataPointClick(dataPoint, seriesItem);
        }
      },
    };
  }, [data, onDataPointClick]);

  return (
    <EChartsContainer
      option={echartsOption}
      notMerge={notMerge}
      className={className}
      style={style}
      error={error}
      loading={loading}
      onEvents={onEvents}
      onChartReady={onChartReady}
    />
  );
};

export default ScalarPieChart;
